import { useId, useState, type FormEvent, type ReactNode } from 'react';
import { DetailDrawer, fieldStyle, StatusDot, token } from '@bp-files/ui';
import { USERS_COPY } from '@/components/Users/users.copy';
import { messageFrom } from '@bp-files/client';
import { SpaceAccessFieldset, type Access } from '@/components/Users/SpaceAccessFieldset';
import { inviteMember, type GrantMode, type SpaceSummary } from '@/lib/api';

/**
 * Invite a member — USR-2, and the only way anybody joins an agency.
 *
 * An address, and the spaces they should reach — each with an access level
 * (PERM-1a), through the same `SpaceAccessFieldset` the grant editor renders.
 * Shared rather than copied, because the two surfaces ask one question and must
 * not be able to answer it differently.
 *
 * **No name field**, and that is the whole of what an admin gives up: the
 * directory already knows what the invitee is called, and a name typed here is
 * one person's guess at how another spells their own. The roster shows the
 * address until they sign in, and their own display name replaces it then
 * (USR-2, ADR-0038).
 *
 * **No cell field.** USR-1/USR-2 still model `cell` as optional on the wire,
 * and `inviteMember` still accepts one — this agency is simply not collecting
 * it, so the form omits the input rather than the API dropping the field.
 *
 * Built on `DetailDrawer`, which is already a modal dialog with a focus trap,
 * Escape handling, and focus restore. Nothing here re-implements any of that.
 *
 * **The client-side checks are a courtesy, not a boundary.** SEC-5 puts
 * authorization and validation in the API, which refuses a blank address and a
 * wrong-domain one whatever this rendered — catching them here only keeps the
 * round trip from being how somebody finds out. The same call `CreateFolder`
 * records for its blank name.
 *
 * **No email is sent.** Inviting a member writes a control-plane row and
 * nothing else (ADR-0040) — there is no directory call to fail, so a failed
 * request means the write itself was refused. Showing the API's own message
 * still reads correctly, exactly as `AddAgencyPanel` does for CON-7's branch
 * B, but the admin is the one who has to tell the invitee to go sign in.
 */

export interface InviteMemberFormProps {
  open: boolean;
  /**
   * `me.agency.domain` — USR-2 requires the address to be on it. Passed rather
   * than fetched: `useMe()` has one call site by design, and a second would be a
   * second session state machine that can disagree with the first.
   */
  agencyDomain: string;
  /** The agency's **Active** spaces. The caller filters; this renders. */
  spaces: readonly SpaceSummary[];
  onClose: () => void;
  /** Called after a successful invitation, so the table can refetch (never patch). */
  onInvited: () => void;
}

/** The part after the last `@`, or `''` if there is no `@` to have one. */
function domainOf(email: string): string {
  const at = email.lastIndexOf('@');
  return at === -1 ? '' : email.slice(at + 1).toLowerCase();
}

const FIELD_CLASS = 'border px-3 py-2 text-sm';

export function InviteMemberForm({
  open,
  agencyDomain,
  spaces,
  onClose,
  onInvited,
}: InviteMemberFormProps) {
  const emailId = useId();
  const emailHintId = useId();

  const [email, setEmail] = useState('');
  /** Every space starts at `No access` — an invitation grants nothing by default (USR-2). */
  const [chosen, setChosen] = useState<ReadonlyMap<string, GrantMode>>(() => new Map());
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | undefined>();

  function setAccess(spaceId: string, access: Access): void {
    setChosen((held) => {
      const next = new Map(held);
      if (access === 'none') next.delete(spaceId);
      else next.set(spaceId, access);
      return next;
    });
  }

  /**
   * `void`-returning, with the async work in a nested function — a submit
   * handler that returns a promise is a floating promise as far as React is
   * concerned.
   */
  function submit(event: FormEvent): void {
    event.preventDefault();
    if (pending) return;

    const address = email.trim();

    if (address === '') {
      setError(USERS_COPY.invite.errors.emailRequired);
      return;
    }

    // Case-insensitively, because a person types what is on a business card and
    // the API compares the same way. Refusing `SMPSO.LA.gov` would be refusing
    // the right answer.
    if (domainOf(address) !== agencyDomain.toLowerCase()) {
      setError(USERS_COPY.invite.errors.wrongDomain(agencyDomain));
      return;
    }

    void send(address);
  }

  async function send(address: string): Promise<void> {
    setPending(true);
    setError(undefined);

    try {
      await inviteMember({
        email: address,
        // Ordered by the list rather than by the order they were changed, so
        // the request body is a function of what is on screen and a test can
        // assert it. A space left at `No access` is absent, not a level.
        grants: spaces.flatMap((space) => {
          const mode = chosen.get(space.id);
          return mode === undefined ? [] : [{ spaceId: space.id, mode }];
        }),
      });

      onInvited();
      onClose();
    } catch (caught) {
      // The refusals here all name something the admin can do: which domain the
      // address must be on, that the person is already on the agency and should
      // be reactivated, or that the invitation did not send and is worth trying
      // again. A generic line would discard the useful half of every one.
      setError(messageFrom(caught, USERS_COPY.invite.errors.failed));
    } finally {
      setPending(false);
    }
  }

  function field(options: {
    id: string;
    hintId: string;
    label: string;
    hint: string;
    type: string;
    name: string;
    value: string;
    onChange: (value: string) => void;
  }): ReactNode {
    return (
      <div className="flex flex-col gap-1.5">
        <label htmlFor={options.id} className="text-xs font-medium">
          {options.label}
        </label>
        <input
          id={options.id}
          name={options.name}
          type={options.type}
          value={options.value}
          aria-describedby={options.hintId}
          autoComplete="off"
          onChange={(event) => {
            options.onChange(event.target.value);
          }}
          className={FIELD_CLASS}
          style={fieldStyle()}
        />
        <span id={options.hintId} className="text-xs" style={{ color: token('bp-text-muted') }}>
          {options.hint}
        </span>
      </div>
    );
  }

  return (
    <DetailDrawer
      open={open}
      onClose={onClose}
      title={USERS_COPY.invite.title}
      closeLabel={USERS_COPY.invite.close}
    >
      <form onSubmit={submit} className="flex flex-col gap-5">
        <p className="text-sm" style={{ color: token('bp-text-muted') }}>
          {USERS_COPY.invite.intro}
        </p>

        {field({
          id: emailId,
          hintId: emailHintId,
          label: USERS_COPY.invite.fields.email,
          // Names the rule before it is broken. The domain is the caller's own,
          // so saying it leaks nothing (CON-11 is about counterparts).
          hint: USERS_COPY.invite.fields.emailHint(agencyDomain),
          type: 'email',
          name: 'email',
          value: email,
          onChange: setEmail,
        })}

        {spaces.length === 0 ? (
          <p className="text-sm" style={{ color: token('bp-text-muted') }}>
            {USERS_COPY.invite.noSpaces}
          </p>
        ) : (
          <SpaceAccessFieldset
            spaces={spaces}
            chosen={chosen}
            onChange={setAccess}
            legend={USERS_COPY.invite.legend}
          />
        )}

        {error === undefined ? null : (
          <p role="alert" className="flex items-start gap-2 text-sm text-danger">
            <StatusDot tone="alert" />
            {error}
          </p>
        )}

        <div className="flex items-center gap-2">
          <button
            type="submit"
            disabled={pending}
            className="border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-60"
            // `bp-on-accent` (white), not `bp-blue-text` — the readme's own
            // line for solid blue buttons. `bp-blue-text` is under 4:1 against
            // this fill, short of WCAG AA's 4.5:1 for text this size.
            style={{
              borderColor: token('bp-blue'),
              background: token('bp-blue-deep'),
              color: token('bp-on-accent'),
              borderRadius: token('radius-md'),
            }}
          >
            {pending ? USERS_COPY.invite.submitting : USERS_COPY.invite.submit}
          </button>

          <button
            type="button"
            onClick={onClose}
            className="border px-3 py-1.5 text-xs font-medium transition-colors"
            style={{
              borderColor: token('bp-border-strong'),
              color: token('bp-text'),
              borderRadius: token('radius-md'),
            }}
          >
            {USERS_COPY.invite.cancel}
          </button>
        </div>
      </form>
    </DetailDrawer>
  );
}
